<template>
  <div ref="authRef" class="login">
    <button v-if="!user" class="login-btn btn btn-gradient" @click="login">
      <img alt="" src="/icons/i-steam-logo.svg" /> {{ t("login") }}
    </button>
    <div v-else-if="user" :class="{ open: showMenu }" class="cselect" @click="showMenu = !showMenu">
      <button class="login__user-btn" type="button">
        <img
            :src="
            user?.avatar_url
              ? getImageFromProxy(avatarUrl || '', '100:100')
              : 'https://ui-avatars.com/api/?name=' + user.persona_name
          "
            alt="user avatar"
            class="login-user-btn__img"
            loading="lazy"
        />
      </button>

      <div class="cselect_dropdown login__dropdown">
        <div class="login__user-info">
          <img
              v-if="user?.plan && user?.plan !== UserPlan.Free"
              :src="`/img/plans/${((user.plan as string) === 'team' ? 'business' : (user.plan as string))?.toLowerCase()}.png`"
              alt=""
              class="user-info__img"
              height="44"
              width="44"
          />
          <div class="user-info__body">
            <p class="user-info__body-title">
              {{ user?.persona_name }}
            </p>
            <p v-if="user?.email" class="user-info__body-subtitle">
              {{ user?.email }}
            </p>
          </div>
        </div>

        <HeaderComponentsBuyTokens />

        <div>
          <HeaderComponentsMainLinks />
          <button class="btn item btn-logout" @click="logoutHandler">
            <svg height="20" width="20">
              <use xlink:href="/icons/sprite.svg#i-logout"></use>
            </svg>
            {{ t("logout") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {useRuntimeConfig} from "nuxt/app";
import {computed, onBeforeUnmount, onMounted} from "vue";
import {useI18n} from "vue-i18n";
import {useStore} from "~/store";
import type {UserFullInfo} from "~/graphql/generated/schema-types";
import {UserPlan} from "~/graphql/generated/schema-types";

const {t} = useI18n();

// use store
const store = useStore();
const user = computed(() => store.user as UserFullInfo | null);
const localePath = useLocalePath();

const config = useRuntimeConfig();
const url = config.public.baseUrlV2;
const showMenu = ref(false);
const authRef = ref(null);

const avatarUrl = computed(() => {
  if (user.value && user.value?.avatar_url) {
    if (user.value?.avatar_url.includes("cdn.csmarketcap.com")) {
      return user.value?.avatar_url;
    }

    const url = user.value?.avatar_url;
    const index = url.lastIndexOf(".");
    if (index === -1) return url;

    const extension = url.substring(index);
    let baseUrl = url.substring(0, index);
    if (baseUrl.endsWith("_full")) {
      baseUrl = baseUrl.slice(0, -5);
    }
    if (user.value.is_admin) {
      return `${baseUrl}${extension}`;
    }
    return `${baseUrl}_medium${extension}`;
  }
  return null;
});
const login = () => {
  return (document.location = url + "/auth/steam");
};
const logoutHandler = async () => {
  try {
    await logout();
    const session = useCookie("session");
    session.value = null;
    store.user = null;
  } catch (e) {
    console.log(e);
  }
};

const documentClick = (event: MouseEvent) => {
  let target = event.target as Element;

  if (authRef.value && authRef.value !== target && !authRef.value.contains(target)) {
    showMenu.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", documentClick);
});

onBeforeUnmount(() => {
  document?.removeEventListener("click", documentClick);
});
</script>

<style lang="scss" scoped>
.login {
  &-btn {
    justify-content: center;
    white-space: nowrap;
    font-weight: 600;
    height: 44px;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    background: var(--accent-primary);
    @media screen and (max-width: 979px) {
      margin-left: 1rem;
      height: 4.3rem;
    }
    @media screen and (max-width: 760px) {
      margin-left: 0;
    }
  }

  &__user-btn {
    padding: 0;
    border-radius: 50%;

    img {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      transition: all 0.3s;
    }
  }

  &__dropdown {
    top: 130% !important;
    right: 0;
    left: unset;
    min-width: max(100%, 221px);
    padding: 16px 16px 8px 16px;

    display: flex !important;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    border-radius: 12px;
    background: var(--surfaces-container);
  }

  &__user-info {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 8px;

    .user-info__img {
      width: 20px;
      height: 20px;
    }

    .user-info__body {
      &-title {
        font-size: 16px;
        font-weight: 600;
        color: var(--surfaces-on-surface);
      }

      &-subtitle {
        font-size: 14px;
        color: var(--surfaces-on-surface-variable);
      }
    }
  }

  .cselect {
    position: relative;

    &.open .login__user-btn {
      border: 1px solid var(--accent-on-surface);
      padding: 4px;

      img {
        width: 36px;
        height: 36px;
      }
    }
  }

  .item.btn-logout {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 0.8rem 0;
    font-weight: 600;
    color: var(--destructive);
    width: 100%;

    svg {
      width: 20px;
      height: 20px;
      color: var(--destructive);
    }

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>
